import { useEffect, useRef } from 'react';
import { promoOffers } from '@/mocks/promos';
import { useToast } from '@/hooks/useToast';

const STORAGE_KEY = 'neonpharma_saved_promos';
const NOTIFIED_KEY = 'neonpharma_promo_notified';

// Thresholds in milliseconds
const THRESHOLDS = [
  { ms: 24 * 60 * 60 * 1000, label: 'менее 24 часов', urgency: 'critical' as const },
  { ms: 3 * 24 * 60 * 60 * 1000, label: '3 дня', urgency: 'warning' as const },
  { ms: 7 * 24 * 60 * 60 * 1000, label: '7 дней', urgency: 'info' as const },
];

const getNotifiedSet = (): Set<string> => {
  try {
    const raw = localStorage.getItem(NOTIFIED_KEY);
    if (raw) return new Set(JSON.parse(raw));
  } catch {
    // ignore
  }
  return new Set();
};

const saveNotifiedSet = (set: Set<string>) => {
  try {
    localStorage.setItem(NOTIFIED_KEY, JSON.stringify([...set]));
  } catch {
    // ignore
  }
};

const getSavedIds = (): number[] => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed;
    }
  } catch {
    // ignore
  }
  return [];
};

export const usePromoExpiryNotifier = () => {
  const { showToast } = useToast();
  const checkedRef = useRef(false);

  useEffect(() => {
    // Only run once per session
    if (checkedRef.current) return;
    checkedRef.current = true;

    // Small delay so the app fully mounts first
    const timer = setTimeout(() => {
      const savedIds = getSavedIds();
      if (savedIds.length === 0) return;

      const notified = getNotifiedSet();
      const now = Date.now();
      let notifiedChanged = false;

      savedIds.forEach((id) => {
        const offer = promoOffers.find((o) => o.id === id);
        if (!offer) return;

        const endsAt = new Date(offer.endsAt).getTime();
        const diff = endsAt - now;

        // Already expired — skip
        if (diff <= 0) return;

        // Find the most urgent threshold that applies
        for (const threshold of THRESHOLDS) {
          if (diff <= threshold.ms) {
            const notifyKey = `${id}_${threshold.urgency}`;
            if (!notified.has(notifyKey)) {
              notified.add(notifyKey);
              notifiedChanged = true;

              const color =
                threshold.urgency === 'critical' ? '#ff2d78' :
                threshold.urgency === 'warning' ? '#ff6b00' :
                '#f0ff00';

              const icon =
                threshold.urgency === 'critical' ? 'ri-alarm-warning-line' :
                'ri-alarm-line';

              showToast({
                type: 'promo_save',
                title:
                  threshold.urgency === 'critical'
                    ? '⚡ Акция почти закончилась!'
                    : threshold.urgency === 'warning'
                    ? '⏰ Акция скоро закончится'
                    : '📌 Напоминание об акции',
                message: `«${offer.title}» — осталось ${threshold.label}`,
                color,
                link: `/promos/${offer.id}`,
                linkLabel: 'Перейти к акции',
              });
            }
            break; // Only show the most urgent threshold
          }
        }
      });

      if (notifiedChanged) saveNotifiedSet(notified);
    }, 1500);

    return () => clearTimeout(timer);
  }, [showToast]);
};